import moment from 'src/app/utils/moment';
import { ProgramWorkoutData } from "./workout.model";

/** "program" = exercise-based days; "split" = muscle-group + target sets per day. */
export type WorkoutProgramKind = "program" | "split";

export interface IWorkoutProgramDetail {
    id: string;
    title: string,
    focus: string,
    goal: string,
    createdBy: string,
    creationDate: string,
    startDate: string,
    startDateHidden: boolean,
    endDate: string,
    endDateHidden: boolean,
    level: string,
    gender: "female" | "male",
    workouts: Partial<ProgramWorkoutData>[],
    notes?: string[],
    readNotes?: boolean;
    /** When "split", days use primary/secondary muscles + sets instead of exercises. */
    workoutProgramKind?: WorkoutProgramKind;
}

export class  WorkoutProgram {
    id: string = null;
    title: string = null;
    description?: string = null;
    comments: any[] = [];
    focus: string = null;
    goal: string = null;
    startDate: string = null;
    startDateHidden: boolean = null;
    endDate: string = null;
    endDateHidden: boolean;
    level: string = null;
    gender: "female" | "male" = null;
    workouts: Partial<ProgramWorkoutData>[] = [];
    createdBy: string = null;
    creationDate: string = null;
    lastModifiedBy: string = null;
    lastModifiedDate: string = null;
    notes: string[] = [];
    organization: string;
    draftWorkoutProgram: any;
    published?: string;
    readNotes?: boolean = null;
    isPremium?: boolean = null;
    /** "program" = exercise-based; "split" = muscle-group + target sets. Omit or undefined = "program". */
    workoutProgramKind?: WorkoutProgramKind;

    constructor (init: Partial<WorkoutProgram>){
        Object.assign(this, init);
    }

    isPublished(): boolean {
        return this.hasOwnProperty("published") && this.hasOwnProperty("workouts") && this.workouts.length > 0
    }

    hasDraft(): boolean {
        return this.hasOwnProperty("draftWorkoutProgram");
    }

    clone(): WorkoutProgram {
        return Object.assign({}, this);
    }

    cloneActive(): IWorkoutProgramDetail {
        const toReturn = {
            id: this.id,
            title: this.title,
            focus: this.focus,
            goal: this.goal,
            startDate: this.startDate,
            startDateHidden: this.startDateHidden,
            createdBy: this.createdBy,
            creationDate: this.creationDate,
            endDate: this.endDate,
            endDateHidden: this.endDateHidden,
            level: this.level,
            gender: this.gender,
            workouts: this.workouts,
            notes: this.notes,
            readNotes: this.readNotes,
            workoutProgramKind: this.workoutProgramKind
        }
        return JSON.parse(JSON.stringify(toReturn));
    }

    cloneDraft(): IWorkoutProgramDetail {
        let obj = Object.assign({}, this.draftWorkoutProgram);
        // Fallback for browser bug
        obj.title = obj.title || this.title;
        obj.notes = obj.notes || this.notes;
        obj.createdBy = this.createdBy || null,
        obj.creationDate = this.creationDate || null,
        obj.id = this.id;
        return obj
    }

    getExerciseIds(): string[] {
        const toReturn = [];
        if(this.workouts) {
            for (const workout of this.workouts) {
                if(workout.sets) {
                    for (const set of workout.sets) {
                        if(set.exercises) {
                            for (const exercise of set.exercises) {
                                if(exercise.id) {
                                    toReturn.push(exercise.id);
                                }
                            }
                        }
                    }
                }
            }
        }
        return toReturn
    }

    isStartDateValid() {
        return !this.startDate || !this.startDateHidden || (moment as any)().isSameOrAfter((moment as any)(this.startDate))
    }

    isEndDateValid() {
        return !this.endDate || !this.endDateHidden || (moment as any)().isSameOrBefore((moment as any)(this.endDate))
    }

    getStatus() {
        if(this.hasDraft()) {
            return "draft"
        } else if(!this.isEndDateValid()) {
            return "expired"
        } else if((moment as any)(this.startDate).isAfter((moment as any)())) {
            return "scheduled"
        } else {
            return "published"
        }
    }
}